import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Select, Upload, message } from 'antd';
import { UploadOutlined, PlusOutlined, DeleteOutlined, FacebookFilled, TwitterOutlined, LinkedinFilled, WhatsAppOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import routes from 'routes';
import { api_srv } from 'services';
import { show_toast } from 'utils/helpers';
import UIOutlet from 'components/common/UIOutlet';
import { useQuery } from 'react-query';
import { fetchIntermediaryProfile } from 'services/apiSrv';

const FormContainer = styled.div`
    max-width: 100%;
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
`;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 600;
    color: #001F5C;
    margin: 0 0 6px 0;
    letter-spacing: -0.02em;
`;

const Divider = styled.div`
    width: 40px;
    height: 3px;
    background: #EF3340;
    border-radius: 2px;
`;

const FormSection = styled.div`
    margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
    font-size: 12px;
    font-weight: 600;
    color: #001F5C;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 12px 0;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 16px;
`;

const DirectorCard = styled.div`
    padding: 16px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    margin-bottom: 12px;
    background: #fafafa;
    position: relative;
`;

const DirectorHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const DirectorTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #001F5C;
`;

const DeleteButton = styled(Button)`
    color: #ff4d4f;
    border-color: #ff4d4f;

    &:hover,
    &:focus {
        color: #ff7875;
        border-color: #ff7875;
    }
`;

const AddDirectorButton = styled(Button)`
    background: #ffffff;
    border: 2px dashed #d9d9d9;
    color: #595959;
    height: 40px;
    font-weight: 500;

    &:hover,
    &:focus {
        border-color: #00B8D4;
        color: #00B8D4;
    }
`;

const ProfileTypeCards = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 16px;
`;

const ProfileCard = styled.div`
    padding: 16px;
    border: 2px solid ${props => props.selected ? '#001F5C' : '#e8e8e8'};
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.selected ? '#F8F9FF' : '#ffffff'};
    text-align: center;

    &:hover {
        border-color: ${props => props.selected ? '#001F5C' : '#00B8D4'};
        background: ${props => props.selected ? '#F8F9FF' : '#FAFBFF'};
    }
`;

const CardIcon = styled.div`
    font-size: 24px;
    margin-bottom: 8px;
    color: ${props => props.selected ? '#001F5C' : '#8c8c8c'};
`;

const CardTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.selected ? '#001F5C' : '#262626'};
    margin-bottom: 2px;
`;

const CardDescription = styled.div`
    font-size: 12px;
    color: #8c8c8c;
`;

const SocialMediaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
`;

const StyledFormItem = styled(Form.Item)`
    margin-bottom: 0;

    .ant-form-item-label > label {
        font-size: 12px;
        font-weight: 500;
        color: #595959;
        height: 20px;
    }

    .ant-input,
    .ant-input-textarea textarea,
    .ant-select-selector {
        border-radius: 6px;
        border-color: #e8e8e8;

        &:focus,
        &:hover {
            border-color: #00B8D4;
        }
    }

    .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
        border-color: #00B8D4;
    }

    .ant-select-focused .ant-select-selector {
        border-color: #00B8D4 !important;
        box-shadow: 0 0 0 2px rgba(0, 184, 212, 0.1);
    }
`;

const UploadSection = styled.div`
    .ant-upload-wrapper {
        width: 100%;
    }

    .ant-btn {
        width: 100%;
        height: 40px;
        border-radius: 6px;
        border: 1px dashed #d9d9d9;
        background: #fafafa;
        color: #595959;
        font-weight: 500;

        &:hover {
            border-color: #00B8D4;
            color: #00B8D4;
        }
    }
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
`;

const SubmitButton = styled(Button)`
    background: #001F5C;
    border-color: #001F5C;
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;

    &:hover,
    &:focus {
        background: #00306B;
        border-color: #00306B;
    }
`;

const CancelButton = styled(Button)`
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;
    border-color: #d9d9d9;
    color: #595959;

    &:hover,
    &:focus {
        border-color: #00B8D4;
        color: #00B8D4;
    }
`;

const initialKycData = {
    name: '',
    country: '',
    pobox: '',
    city: '',
    profileType: 'LTD',
    documents: [
        { name: 'certofinccoperation', path: '' },
        { name: 'krapin', path: '' },
    ],
    directors: [
        { names: '', country: '', salutation: '', pin: '' }
    ],
    socialMedia: {
        facebook: '',
        twitter: '',
        linkedin: '',
        whatsapp: '',
    }
};

// File upload function
const uploadProductImage = async (options) => {
    const { onSuccess, onError, file } = options;

    try {
        let res = await (await api_srv).upload_api("productimage", file);
        onSuccess(res.data[0].fileUrl);
        show_toast(`${file.name} uploaded successfully.`, "success");
        return res.data[0].fileUrl;
    } catch (err) {
        console.error("File upload error:", err);
        onError(err);
        show_toast(`${file.name} upload failed.`, "error");
    }
};

function KycSetup() {
    const [form] = Form.useForm();
    const [kyc, setKyc] = useState(initialKycData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedProfileType, setSelectedProfileType] = useState('');
    const history = useHistory();

    console.log("profile type", selectedProfileType)

    // Fetch initial profile data
    const { data: profileData, isLoading } = useQuery('profile', fetchIntermediaryProfile, {
        onError: (error) => {
            message.error('Failed to fetch profile data');
            console.error('Fetch error:', error);
        },
    });

    useEffect(() => {
        if (profileData?.data?.[0]?.profileType === "COMPANY") {
            setSelectedProfileType('LTD');
        } else if (profileData?.data?.[0]?.profileType === "INDIVIDUAL") {
            setSelectedProfileType('SOLE_PROPRIETOR');
        } else {
            setSelectedProfileType('LTD'); // Default to LTD if no valid profile type
        }
    }, [profileData]);

    // Initialize form fields only once on component mount
    useEffect(() => {
        form.setFieldsValue({
            ...kyc,
            'Kra Pin Certificate': (kyc?.documents ?? [])?.find(doc => doc.name === 'krapin')?.path,
            'Incorporation Certificate': (kyc?.documents ?? [])?.find(doc => doc.name === 'certofinccoperation')?.path,
        });
    }, [form, kyc]);

    // Sync kyc state with form changes
    const onValuesChange = (changedValues, allValues) => {
        setKyc(prev => ({
            ...prev,
            ...allValues,
            documents: prev.documents.map(doc => {
                if (doc.name === 'krapin' && allValues['Kra Pin Certificate']) {
                    return { ...doc, path: allValues['Kra Pin Certificate'] };
                }
                if (doc.name === 'certofinccoperation' && allValues['Incorporation Certificate']) {
                    return { ...doc, path: allValues['Incorporation Certificate'] };
                }
                return doc;
            })
        }));
    };

    const onFinish = async (values) => {
        setIsSubmitting(true);
        try {
            let updatedKyc;
            if (selectedProfileType === 'SOLE_PROPRIETOR') {
                updatedKyc = {
                    name: values.name,
                    country: values.country,
                    profileType: selectedProfileType,
                    // socialMedia: values.socialMedia || kyc.socialMedia
                    socialMedia: {
                    ...kyc.socialMedia,
                    ...values.socialMedia,
                    }
                };
            } else {
                const updatedDocuments = [
                    { name: 'krapin', path: values['Kra Pin Certificate'] || '' },
                    { name: 'certofinccoperation', path: values['Incorporation Certificate'] || '' }
                ];

                updatedKyc = {
                    ...kyc,
                    documents: updatedDocuments,
                    pobox: values.pobox || kyc.pobox,
                    country: values.country || kyc.country,
                    city: values.city || kyc.city,
                    directors: values.directors || kyc.directors,
                    profileType: selectedProfileType,
                    // socialMedia: values.socialMedia || kyc.socialMedia
                    socialMedia: {
                    ...kyc.socialMedia,
                    ...values.socialMedia,
                    }
                };
            }

            setKyc(updatedKyc);

            await (await api_srv).updateIntermediaryKyc(updatedKyc);

            show_toast('KYC data submitted successfully.', 'success');
            history.push("/dashboard");
        } catch (error) {
            console.error('Submission failed:', error);
            show_toast('KYC submission failed.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onFinishFailed = (errorInfo) => {
        console.log('Form validation failed:', errorInfo);
        show_toast('Please correct the errors in the form.', 'error');
    };

    const addDirector = () => {
        const newDirectors = [...kyc.directors, { names: '', country: '', salutation: '', pin: '' }];
        setKyc(prev => ({
            ...prev,
            directors: newDirectors
        }));
        form.setFieldsValue({
            directors: newDirectors
        });
    };

    const removeDirector = (index) => {
        if (kyc.directors.length === 1) {
            message.warning('At least one director is required');
            return;
        }
        const newDirectors = (kyc.directors ?? [])?.filter((_, i) => i !== index);
        setKyc(prev => ({
            ...prev,
            directors: newDirectors
        }));
        form.setFieldsValue({
            directors: newDirectors
        });
    };

    // Custom upload handler for each document
    const handleUpload = (documentName, fieldName) => {
        return {
            customRequest: async ({ file, onSuccess, onError }) => {
                try {
                    const fileUrl = await uploadProductImage({ file, onSuccess, onError });
                    console.log("file url", fileUrl)
                    if (fileUrl) {
                        setKyc(prev => ({
                            ...prev,
                            documents: prev.documents.map(doc =>
                                doc.name === documentName ? { ...doc, path: fileUrl } : doc
                            )
                        }));
                        form.setFieldsValue({ [fieldName]: fileUrl });
                    }
                } catch (err) {
                    console.error('Upload failed:', err);
                }
            }
        };
    };

    return (
        <UIOutlet
            title="KYC Setup"
            logoSrc="https://res.cloudinary.com/doifxgf1h/image/upload/v1768298670/Nexus_Versions_FA-01-removebg-preview_mcak7j.png"
            bannerTitle="Complete Your KYC"
            bannerDescription="Provide your information for verification"
        >
            <FormContainer>
                <PageHeader>
                    <Title>KYC Information</Title>
                    <Divider />
                </PageHeader>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    onValuesChange={onValuesChange}
                >
                    {/* Profile Type Selection */}
                    <FormSection>
                        <SectionTitle>Profile Type</SectionTitle>
                        <ProfileTypeCards>
                            {selectedProfileType === 'LTD' && (
                                <ProfileCard
                                    selected={true}
                                    onClick={() => setSelectedProfileType('LTD')}
                                >
                                    <CardIcon selected={true}>🏢</CardIcon>
                                    <CardTitle selected={true}>Limited Company</CardTitle>
                                    <CardDescription>LTD</CardDescription>
                                </ProfileCard>
                            )}
                            {selectedProfileType === 'SOLE_PROPRIETOR' && (
                                <ProfileCard
                                    selected={true}
                                    onClick={() => setSelectedProfileType('SOLE_PROPRIETOR')}
                                >
                                    <CardIcon selected={true}>👤</CardIcon>
                                    <CardTitle selected={true}>Sole Proprietor</CardTitle>
                                    <CardDescription>Individual Business</CardDescription>
                                </ProfileCard>
                            )}
                        </ProfileTypeCards>
                    </FormSection>

                    {/* Information Section */}
                    <FormSection>
                        <SectionTitle>{selectedProfileType === 'SOLE_PROPRIETOR' ? 'Sole Proprietor Information' : 'Company Information'}</SectionTitle>
                        <FormGrid>
                            <StyledFormItem
                                label={selectedProfileType === 'SOLE_PROPRIETOR' ? 'Full Name' : 'Company Name'}
                                name="name"
                                rules={[{ required: true, message: `Please enter ${selectedProfileType === 'SOLE_PROPRIETOR' ? 'your full name' : 'company name'}` }]}
                            >
                                <Input placeholder={selectedProfileType === 'SOLE_PROPRIETOR' ? 'Enter full name' : 'Enter company name'} />
                            </StyledFormItem>

                            <StyledFormItem
                                label="Country"
                                name="country"
                                rules={[{ required: true, message: 'Please select a country' }]}
                            >
                                <Select
                                    placeholder="Select country"
                                    options={[
                                        { value: 'kenya', label: 'Kenya' },
                                        { value: 'uganda', label: 'Uganda' },
                                        { value: 'tanzania', label: 'Tanzania' },
                                        { value: 'rwanda', label: 'Rwanda' },
                                        { value: 'burundi', label: 'Burundi' }
                                    ]}
                                />
                            </StyledFormItem>

                            {selectedProfileType === 'LTD' && (
                                <>
                                    <StyledFormItem
                                        label="City"
                                        name="city"
                                        rules={[{ required: true, message: 'Please enter city' }]}
                                    >
                                        <Input placeholder="Enter city" />
                                    </StyledFormItem>

                                    <StyledFormItem
                                        label="PO Box"
                                        name="pobox"
                                        rules={[{ required: true, message: 'Please enter PO Box' }]}
                                    >
                                        <Input placeholder="Enter PO Box" />
                                    </StyledFormItem>
                                </>
                            )}
                        </FormGrid>
                    </FormSection>

                    {/* Social Media Section */}
                    <FormSection>
                        <SectionTitle>Social Media Links</SectionTitle>
                        <SocialMediaGrid>
                            <StyledFormItem
                                label="Facebook"
                                name={['socialMedia', 'facebook']}
                                rules={[{ required: false, message: 'Please enter Facebook URL' }]}
                            >
                                <Input
                                    placeholder="Facebook profile URL"
                                    prefix={<FacebookFilled style={{ color: '#1877F2' }} />}
                                />
                            </StyledFormItem>

                            <StyledFormItem
                                label="Twitter"
                                name={['socialMedia', 'twitter']}
                                rules={[{ required: false, message: 'Please enter Twitter URL' }]}
                            >
                                <Input
                                    placeholder="Twitter profile URL"
                                    prefix={<TwitterOutlined style={{ color: '#1DA1F2' }} />}
                                />
                            </StyledFormItem>

                            <StyledFormItem
                                label="LinkedIn"
                                name={['socialMedia', 'linkedin']}
                                rules={[{ required: false, message: 'Please enter LinkedIn URL' }]}
                            >
                                <Input
                                    placeholder="LinkedIn profile URL"
                                    prefix={<LinkedinFilled style={{ color: '#0A66C2' }} />}
                                />
                            </StyledFormItem>

                            <StyledFormItem
                                label="WhatsApp"
                                name={['socialMedia', 'whatsapp']}
                                rules={[
                                    { required: false },
                                    { pattern: /^[+]?[0-9]{10,15}$/, message: 'Please enter a valid phone number (e.g., +254712345678)' }
                                ]}
                            >
                                <Input
                                    placeholder="WhatsApp phone number (e.g., +254712345678)"
                                    prefix={<WhatsAppOutlined style={{ color: '#25D366' }} />}
                                />
                            </StyledFormItem>
                        </SocialMediaGrid>
                    </FormSection>

                    {/* Document Uploads Section (LTD only) */}
                    {selectedProfileType === 'LTD' && (
                        <FormSection>
                            <SectionTitle>Required Documents</SectionTitle>
                            <FormGrid>
                                <UploadSection>
                                    <StyledFormItem
                                        label="KRA Pin Certificate"
                                        name="Kra Pin Certificate"
                                        rules={[{ required: true, message: 'KRA Pin Certificate is required' }]}
                                    >
                                        <Upload
                                            {...handleUpload('krapin', 'Kra Pin Certificate')}
                                            accept=".pdf,.doc,.docx"
                                            maxCount={1}
                                            listType="text"
                                        >
                                            <Button icon={<UploadOutlined />}>
                                                Upload KRA Pin Certificate
                                            </Button>
                                        </Upload>
                                    </StyledFormItem>
                                </UploadSection>

                                <UploadSection>
                                    <StyledFormItem
                                        label="Incorporation Certificate"
                                        name="Incorporation Certificate"
                                        rules={[{ required: true, message: 'Incorporation Certificate is required' }]}
                                    >
                                        <Upload
                                            {...handleUpload('certofinccoperation', 'Incorporation Certificate')}
                                            accept=".pdf,.doc,.docx"
                                            maxCount={1}
                                            listType="text"
                                        >
                                            <Button icon={<UploadOutlined />}>
                                                Upload Incorporation Certificate
                                            </Button>
                                        </Upload>
                                    </StyledFormItem>
                                </UploadSection>
                            </FormGrid>
                        </FormSection>
                    )}

                    {/* Directors Section (LTD only) */}
                    {selectedProfileType === 'LTD' && (
                        <FormSection>
                            <SectionTitle>Directors Information</SectionTitle>
                            {kyc.directors.map((director, index) => (
                                <DirectorCard key={index}>
                                    <DirectorHeader>
                                        <DirectorTitle>Director {index + 1}</DirectorTitle>
                                        {kyc.directors.length > 1 && (
                                            <DeleteButton
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => removeDirector(index)}
                                            >
                                                Remove
                                            </DeleteButton>
                                        )}
                                    </DirectorHeader>

                                    <FormGrid>
                                        <StyledFormItem
                                            label="Salutation"
                                            name={['directors', index, 'salutation']}
                                            rules={[{ required: true, message: 'Please select salutation' }]}
                                        >
                                            <Select
                                                placeholder="Select salutation"
                                                options={[
                                                    { value: 'Mr', label: 'Mr' },
                                                    { value: 'Ms', label: 'Ms' },
                                                    { value: 'Mrs', label: 'Mrs' },
                                                    { value: 'Dr', label: 'Dr' }
                                                ]}
                                            />
                                        </StyledFormItem>

                                        <StyledFormItem
                                            label="Full Name"
                                            name={['directors', index, 'names']}
                                            rules={[{ required: true, message: 'Please enter full name' }]}
                                        >
                                            <Input placeholder="Enter full name" />
                                        </StyledFormItem>

                                        <StyledFormItem
                                            label="Country"
                                            name={['directors', index, 'country']}
                                            rules={[{ required: true, message: 'Please select country' }]}
                                        >
                                            <Select
                                                placeholder="Select country"
                                                options={[
                                                    { value: 'kenya', label: 'Kenya' },
                                                    { value: 'uganda', label: 'Uganda' },
                                                    { value: 'tanzania', label: 'Tanzania' },
                                                    { value: 'rwanda', label: 'Rwanda' },
                                                    { value: 'burundi', label: 'Burundi' }
                                                ]}
                                            />
                                        </StyledFormItem>

                                        <StyledFormItem
                                            label="PIN Number"
                                            name={['directors', index, 'pin']}
                                            rules={[
                                                { required: true, message: 'Please enter PIN number' },
                                                { pattern: /^[A-Z0-9]+$/, message: 'PIN must contain only uppercase letters and numbers' }
                                            ]}
                                        >
                                            <Input placeholder="Enter PIN number" />
                                        </StyledFormItem>
                                    </FormGrid>
                                </DirectorCard>
                            ))}

                            <AddDirectorButton
                                type="dashed"
                                onClick={addDirector}
                                block
                                icon={<PlusOutlined />}
                            >
                                Add Another Director
                            </AddDirectorButton>
                        </FormSection>
                    )}

                    <ActionButtons>
                        <CancelButton onClick={() => history.push(routes.profilesetup)}>
                            Back
                        </CancelButton>
                        <SubmitButton
                            type="primary"
                            htmlType="submit"
                            loading={isSubmitting}
                        >
                            Submit KYC
                        </SubmitButton>
                    </ActionButtons>
                </Form>
            </FormContainer>
        </UIOutlet>
    );
}

export default KycSetup;